import { useRouter } from 'next/router';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { trackAction, usePlatform } from '../../../helpers/tracking';
import Transition from '../../Transition/Transition.jsx';

import './UserSettings.scss';
import Popup from '../Popup/Popup.jsx';
import Portal from '../../Portal/Portal.jsx';
import { isEmail } from '../../../helpers/form';
import translate from '../../../helpers/translate';
import icons from '../../../shared/assets/icons';
import { getString } from '../../../helpers/styles';
import BackButton from '../../BackButton/BackButton.jsx';
import DatePickerComponent from '../DatePicker/DatePicker.jsx';
import redesignedIcons from '../../../shared/assets/icons/redesign';

const { Check, Search, LoadMore, Info } = redesignedIcons;
const { Exclamation, RightArrow } = icons;

const userStatus = {
  PREGNANT: 'Nu zwanger',
  DELIVERED: 'Bevallen',
  ENDED: 'Zwangerschap vroegtijdig beëindigd',
  PREPARING: 'Voorbereiding op zwangerschap',
  OTHER: 'Anders'
};

const CONTACTS_URL = 'https://www.zwangerenportaal.nl/contacts';

function validateContact(type, raw) {
  if (type === 'email') {
    if (!raw) return { ok: false, error: 'Vul het e-mailadres in.' };
    if (!isEmail(raw))
      return { ok: false, error: 'Voer een geldig e-mailadres in.' };
    return { ok: true, value: raw };
  }

  const digitsOnly = (raw || '').replace(/\D/g, '');

  if (!digitsOnly) return { ok: false, error: 'Vul het telefoonnummer in.' };

  // Supported formats:
  // 6xxxxxxxx        -> 00316xxxxxxxx
  // 06xxxxxxxx       -> 00316xxxxxxxx
  // +316xxxxxxxx     -> 00316xxxxxxxx
  // 316xxxxxxxx      -> 00316xxxxxxxx
  // 00316xxxxxxxx    -> 00316xxxxxxxx
  let normalized = null;

  if (/^00316\d{8}$/.test(digitsOnly)) {
    normalized = digitsOnly;
  } else if (/^316\d{8}$/.test(digitsOnly)) {
    normalized = `00${digitsOnly}`;
  } else if (/^06\d{8}$/.test(digitsOnly)) {
    normalized = `0031${digitsOnly.slice(1)}`;
  } else if (/^6\d{8}$/.test(digitsOnly)) {
    normalized = `0031${digitsOnly}`;
  }

  if (!normalized) {
    return {
      ok: false,
      error: 'Voer een geldig telefoonnummer in (06........).'
    };
  }

  return { ok: true, value: normalized };
}

export default function UserSettings({
  user,
  locale = 'nl',
  organizations,
  changeUserData,
  searchOrganizations,
  organizationsLoading,
  updateEmail,
  updateMobile
}) {
  const router = useRouter();
  const platform = usePlatform();
  const t = (key) => translate(key, locale);

  const hash = router.asPath.split('#')[1];

  const [data, setData] = useState({
    edd: user.edd,
    email: user.email,
    status: user.status,
    mobile: user.mobile,
    provider: user.provider,
    emailPartner: user.emailPartner,
    pregnancyEndDate: user.pregnancyEndDate
  });

  const [label, date, period] = getDateAttributes(data);

  const postCodeInput = useRef();
  const providerEmailField = useRef();
  const saveContactLockRef = useRef(false);

  const [provider, setProvider] = useState(null);
  const [postCode, setPostCode] = useState(user.postcode);
  const [providerListSize, setProviderListSize] = useState(4);

  const [contactModal, setContactModal] = useState({
    open: false,
    type: null,
    value: '',
    initialValue: '',
    error: '',
    result: null,
    isSaving: false,
    statusText: 'Opslaan'
  });

  const [emailPartnerPopup, showEmailPartnerPopup] = useState(false);

  const [initialDate, changeInitialDate] = useState(
    date && {
      day: new Date(date).getDate(),
      year: new Date(date).getFullYear(),
      month: new Date(date).getMonth() + 1
    }
  );

  const isContactSaveDisabled =
    !contactModal.value ||
    contactModal.isSaving ||
    contactModal.value === contactModal.initialValue;

  useEffect(() => {
    window.scroll({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSaveContact = useCallback(async () => {
    if (saveContactLockRef.current) return;
    if (isContactSaveDisabled) return;

    saveContactLockRef.current = true;

    try {
      const raw = (contactModal.value || '').trim();
      const validation = validateContact(contactModal.type, raw);

      if (!validation.ok) {
        setContactModal((prev) => ({ ...prev, error: validation.error }));
        return;
      }

      setContactModal((prev) => ({
        ...prev,
        isSaving: true,
        error: '',
        statusText: 'Opslaan',
        result: null
      }));

      const config =
        contactModal.type === 'email'
          ? {
              track: 'updateemail',
              mutate: () =>
                updateEmail({ variables: { email: validation.value } }),
              pickResult: (resp) => resp?.updateEmail,
              applyLocalUpdate: () =>
                setData((d) => ({ ...d, email: validation.value })),
              successValue: validation.value
            }
          : {
              track: 'updatemobile',
              mutate: () =>
                updateMobile({ variables: { mobile: validation.value } }),
              pickResult: (resp) => resp?.updateMobile,
              applyLocalUpdate: () =>
                setData((d) => ({ ...d, mobile: validation.value })),
              successValue: validation.value
            };

      trackAction(platform, 'settings', config.track);

      const { data: resp } = await config.mutate();
      const result = config.pickResult(resp) || {};

      if (!String(result.statusCode).startsWith('2')) {
        setContactModal((prev) => ({
          ...prev,
          result,
          isSaving: false,
          statusText: 'Opslaan'
        }));
        return;
      }

      config.applyLocalUpdate();

      setContactModal((prev) => ({
        ...prev,
        result: { ...result, statusCode: result.statusCode || 200 },
        isSaving: false,
        initialValue: config.successValue,
        value: config.successValue,
        statusText: 'Opgeslagen'
      }));
    } catch (e) {
      setContactModal((prev) => ({
        ...prev,
        result: {
          statusCode: 'ERROR'
        },
        isSaving: false,
        statusText: 'Opslaan'
      }));
    } finally {
      saveContactLockRef.current = false;
    }
  }, [
    contactModal.type,
    contactModal.value,
    isContactSaveDisabled,
    platform,
    updateEmail,
    updateMobile
  ]);

  switch (hash) {
    case 'status':
      return (
        <div className="userSettings">
          <BackButton />

          <h3 className="userSettings-section-title">Zwangerschap</h3>

          <p className="userSettings-field-title">Huidige Status</p>

          <div className="userSettings-field userSettings-field-disabled">
            {userStatus[data.status]}
          </div>

          <UserSettingsDateItem
            data={data}
            date={date}
            period={period}
            setData={setData}
            platform={platform}
            initialDate={initialDate}
            changeUserData={changeUserData}
            changeInitialDate={changeInitialDate}
          />

          <p className="userSettings-field-title">Status aanpassen naar</p>

          <ul className="userSettings-field-list">
            {Object.values(userStatus).map((val) => (
              <li
                onClick={(e) => {
                  const status = e.target.dataset.id;

                  trackAction(platform, 'settings', 'setstatus');

                  changeUserData({
                    variables: {
                      status,
                      edd: null,
                      pregnancyEndDate: null
                    }
                  });

                  changeInitialDate(null);

                  setData({
                    ...data,
                    edd: null,
                    pregnancyEndDate: null,
                    status
                  });
                }}
                className={getString([
                  { value: 'userSettings-field-list-item', condition: true },
                  {
                    value: 'userSettings-field-list-item-active',
                    condition: val === userStatus[data.status]
                  }
                ])}
                data-id={Object.keys(userStatus).find(
                  (key) => userStatus[key] === val
                )}
                key={Object.keys(userStatus).find(
                  (key) => userStatus[key] === val
                )}
              >
                {val}
                {val === userStatus[data.status] && (
                  <Check className="userSettings-field-list-item-active-icon" />
                )}
              </li>
            ))}
          </ul>
        </div>
      );

    case 'status-date':
      return (
        <div className="userSettings">
          <BackButton />

          <h3 className="userSettings-section-title">Instellingen</h3>

          <UserSettingsDateItem
            data={data}
            date={date}
            period={period}
            setData={setData}
            platform={platform}
            initialDate={initialDate}
            changeUserData={changeUserData}
            changeInitialDate={changeInitialDate}
          />
        </div>
      );

    case 'provider':
      return (
        <div className="userSettings">
          <BackButton />

          <h3 className="userSettings-provider-title">Kies je zorgverlener</h3>

          <h5 className="userSettings-provider-subtitle">
            Door je account te koppelen aan je zorgverlener houden we je op de
            hoogte van je voortgang en kan je makkelijk contact met hen opnemen
          </h5>

          <form
            className="userSettings-provider-search-form"
            onSubmit={(e) => {
              e.preventDefault();
              searchOrganizations({
                variables: {
                  postalcode: e.target.code.value,
                  distance: 1000
                }
              });
            }}
          >
            <input
              name="code"
              type="number"
              pattern="[0-9]+"
              value={postCode}
              ref={postCodeInput}
              onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
              onChange={(e) =>
                e.target.value.length <= 4 && setPostCode(e.target.value)
              }
              placeholder="Zoek op postcode"
              className="userSettings-provider-input userSettings-provider-search-input"
            />

            <Search
              onClick={() => {
                searchOrganizations({
                  variables: {
                    postalcode: postCodeInput.current.value,
                    distance: 1000
                  }
                });
              }}
              className="userSettings-provider-search-icon"
            />

            <button
              className="userSettings-button userSettings-provider-search-button"
              onClick={() => {
                trackAction(platform, 'settings', 'searchprovider');

                setProvider(null);

                searchOrganizations({
                  variables: {
                    postalcode: postCodeInput.current.value,
                    distance: 1000
                  }
                });
              }}
            >
              Zoeken
            </button>
          </form>

          {organizationsLoading && (
            <p className="userSettings-provider-info-message">Laden...</p>
          )}

          {organizations && !organizations?.length && (
            <p className="userSettings-provider-info-message">
              <Info className="userSettings-provider-info-message-icon" />
              We hebben geen zorgverleners kunnen vinden binnen deze postcode
            </p>
          )}

          {!!organizations?.length && (
            <>
              <p className="userSettings-provider-info-message">
                <span className="userSettings-provider-text-bold">
                  {organizations?.length} zorgverleners&nbsp;
                </span>
                bij jou in de buurt:
              </p>

              <ul className="userSettings-provider-list">
                {organizations.slice(0, providerListSize).map((item) => (
                  <li
                    key={item.id}
                    className={getString([
                      {
                        value: 'userSettings-provider-list-item',
                        condition: true
                      },
                      {
                        value: 'userSettings-provider-list-item-active',
                        condition: provider?.accountName === item.accountName
                      }
                    ])}
                  >
                    <label>
                      <input
                        type="radio"
                        onChange={() => {}}
                        className="userSettings-provider-radio-input"
                        onClick={() => setProvider(item)}
                        checked={provider?.accountName === item.accountName}
                      />

                      <span className="userSettings-provider-checkmark userSettings-provider-checkmark">
                        <Check className="userSettings-provider-checkmark-icon" />
                      </span>

                      <OrganizationCard
                        logoUrl={item.logoUrl}
                        name={item.displayName}
                        distance={item.distance}
                      />
                    </label>
                  </li>
                ))}
              </ul>

              {providerListSize < organizations.length && (
                <button
                  onClick={() => {
                    trackAction(platform, 'settings', 'loadmoreproviders');
                    setProviderListSize(providerListSize + 4);
                  }}
                  className="userSettings-provider-loadmore-button"
                >
                  <LoadMore className="userSettings-provider-loadmore-icon" />
                  Laad meer
                </button>
              )}
            </>
          )}

          <button
            className={getString([
              {
                value: 'userSettings-button userSettings-provider-button',
                condition: true
              },
              { value: 'userSettings-button-disabled', condition: !provider }
            ])}
            onClick={() => {
              if (provider) {
                trackAction(platform, 'settings', 'setprovider');

                changeUserData({
                  variables: {
                    weekMail: 'Y',
                    provider: provider.accountName,
                    providerStatus: 'OK'
                  }
                });

                setData({
                  ...data,
                  provider
                });

                router.push('/profile-settings');
              }
            }}
          >
            Kies zorgverlener
          </button>
        </div>
      );

    default:
      return (
        <div className="userSettings">
          <BackButton />

          <div>
            <h3 className="userSettings-section-title">Instellingen</h3>

            <div className="userSettings-section">
              <h3 className="userSettings-section-title">Zwangerschap</h3>

              <p className="userSettings-field-title">Status</p>

              <UserSettingsItem
                text={userStatus[data.status]}
                onClick={() => router.push('/profile-settings#status')}
              />

              <UserSettingsDateItem
                data={data}
                platform={platform}
                onClick={() => router.push('/profile-settings#status-date')}
              />
            </div>

            <div className="userSettings-section">
              <h3 className="userSettings-section-title">Zorgverlener(s)</h3>

              <p className="userSettings-field-title">Primaire zorgverlener</p>

              <button
                onClick={() => router.push('/profile-settings#provider')}
                className="userSettings-provider-field userSettings-field"
              >
                {data.provider ? (
                  <>
                    <img
                      className="userSettings-provider-field-img"
                      src={data.provider.logoUrl}
                      alt="provider-logo"
                    />
                    <span className="userSettings-provider-field-value">
                      {data.provider.displayName}
                    </span>
                  </>
                ) : (
                  <span className="userSettings-provider-field-value">
                    Onbekend
                  </span>
                )}
                <RightArrow className="userSettings-field-icon" />
              </button>

              {!data.provider && (
                <p className="userSettings-warning">
                  <Exclamation className="userSettings-warning-icon" />
                  Je hebt geen primaire zorgverlener toegevoegd
                </p>
              )}
            </div>

            <div className="userSettings-section">
              <h3 className="userSettings-section-title">Profiel</h3>

              <p className="userSettings-field-title">E-mailadres</p>

              <UserSettingsItem
                onClick={() => {
                  setContactModal({
                    open: true,
                    type: 'email',
                    value: data.email || '',
                    initialValue: data.email || '',
                    error: '',
                    result: null,
                    isSaving: false,
                    statusText: 'Opslaan'
                  });
                }}
                text={data.email ? <>{data.email}</> : 'Onbekend'}
              />

              <p className="userSettings-field-title">Telefoon nummer</p>

              <UserSettingsItem
                onClick={() => {
                  setContactModal({
                    open: true,
                    type: 'phone',
                    value: data.mobile || '',
                    initialValue: data.mobile || '',
                    error: '',
                    result: null,
                    isSaving: false,
                    statusText: 'Opslaan'
                  });
                }}
                text={data.mobile ? <>{data.mobile}</> : 'Onbekend'}
              />

              <p className="userSettings-field-title">Accountbeveiliging</p>

              <a
                href="https://id.zwangerenportaal.nl/portal/mfa"
                target="_blank"
                rel="noopener noreferrer"
                className="userSettings-button"
                onClick={() =>
                  trackAction(platform, 'settings', 'updateaccountsecurity')
                }
              >
                Wijzigen
              </a>

              <p className="userSettings-field-title">Wachtwoord</p>

              <a
                target="_blank"
                rel="noopener noreferrer"
                className="userSettings-button"
                href="https://id.zwangerenportaal.nl/password-reset"
                onClick={() =>
                  trackAction(platform, 'settings', 'updatepassword')
                }
              >
                Wijzigen
              </a>

              <p className="userSettings-field-title">E-mail voorkeuren</p>

              <a
                target="_blank"
                rel="noopener noreferrer"
                className="userSettings-button"
                href={user.emailPreferencesUrl}
                onClick={() =>
                  trackAction(platform, 'settings', 'updateemailpreferences')
                }
              >
                Wijzigen
              </a>

              <p className="userSettings-field-title">{t('Delete account')}</p>

              <a
                target="_blank"
                rel="noopener noreferrer"
                className="userSettings-button"
                href="https://www.zwangerapp.nl/account-verwijderen"
                onClick={() =>
                  trackAction(platform, 'settings', 'deleteaccount')
                }
              >
                {t('Delete')}
              </a>
            </div>

            <div className="userSettings-section">
              <h3 className="userSettings-section-title">Gezin</h3>

              <p className="userSettings-field-title">E-mail adress partner</p>

              <UserSettingsItem
                onClick={() => showEmailPartnerPopup(true)}
                text={data.emailPartner || 'Onbekend'}
              />
            </div>

            <Portal>
              <Transition on={contactModal.open}>
                <Popup>
                  <>
                    <div className="popup-header">
                      <h4>
                        {contactModal.type === 'email'
                          ? 'Wijzig het e-mailadres'
                          : 'Wijzig het telefoonnummer'}
                      </h4>
                      <button
                        className="popup-close-button"
                        onClick={() =>
                          setContactModal((prev) => ({ ...prev, open: false }))
                        }
                      >
                        &times;
                      </button>
                    </div>

                    <div className="popup-body userSettings-popup-body">
                      <label className="userSettings-field-title userSettings-popup-field-title">
                        {contactModal.type === 'email'
                          ? 'Nieuw e-mailadres'
                          : 'Nieuw telefoonnummer'}
                        <input
                          autoFocus
                          className="userSettings-input-field"
                          type={contactModal.type === 'email' ? 'email' : 'tel'}
                          placeholder={
                            contactModal.type === 'phone'
                              ? '06........'
                              : undefined
                          }
                          value={contactModal.value}
                          onChange={(e) =>
                            setContactModal((prev) => ({
                              ...prev,
                              value: e.target.value,
                              error: '',
                              result: null,
                              statusText: 'Opslaan'
                            }))
                          }
                        />
                      </label>

                      <div className="userSettings-error-slot">
                        {contactModal.error && (
                          <span className="userSettings-error">
                            {contactModal.error}
                          </span>
                        )}
                      </div>

                      <button
                        className={getString([
                          { value: 'userSettings-button', condition: true },
                          {
                            value: 'userSettings-button-disabled',
                            condition: isContactSaveDisabled
                          }
                        ])}
                        disabled={isContactSaveDisabled}
                        onClick={handleSaveContact}
                      >
                        {contactModal.isSaving ? (
                          <>
                            <span className="btn-spinner" />
                            Opslaan...
                          </>
                        ) : (
                          contactModal.statusText
                        )}
                      </button>

                      {contactModal.result && (
                        <div
                          className={getString([
                            { value: 'userSettings-info', condition: true },
                            {
                              value: 'userSettings-info-success',
                              condition:
                                contactModal.result.statusCode &&
                                String(
                                  contactModal.result.statusCode
                                ).startsWith('2')
                            },
                            {
                              value: 'userSettings-info-error',
                              condition:
                                !contactModal.result.statusCode ||
                                !String(
                                  contactModal.result.statusCode
                                ).startsWith('2')
                            }
                          ])}
                          style={{ marginTop: 12 }}
                        >
                          <p style={{ margin: 0 }}>
                            <span>Status:</span>{' '}
                            {contactModal.result.statusCode || '-'}
                          </p>
                          <p style={{ margin: 0 }}>
                            <span>Message: </span>
                            {(() => {
                              const code = Number(
                                contactModal.result.statusCode
                              );
                              const type = contactModal.type;

                              switch (code) {
                                case 200:
                                  return type === 'email'
                                    ? "Hartelijk dank voor je bericht. We hebben jouw e-mailadres gewijzigd. Na verificatie kan je weer inloggen en kan jouw verloskundige het dossier met je delen / we've changed your email address. After verification, you can log in again, and your midwife can share the file with you."
                                    : "Hartelijk dank voor je bericht. We hebben jouw telefoonnummer gewijzigd. Na verificatie kan je weer inloggen en kan jouw verloskundige het dossier met je delen / we've changed your mobile number. After verification, you can log in again, and your midwife can share the file with you.";

                                case 409:
                                  return type === 'email'
                                    ? 'We konden je verzoek niet verwerken –\nE-mailadres is al in gebruik / We could not process your request – email address is already in use'
                                    : 'We konden je verzoek niet verwerken –\nTelefoon nummer is al in gebruik / We could not process your request – phone number is already in use';

                                case 400:
                                  return type === 'email'
                                    ? 'We konden je verzoek niet verwerken –\nformaat E-mailadres incorrect / Incorrect email address format'
                                    : 'We konden je verzoek niet verwerken –\nformaat Telefoon nummer incorrect / Incorrect phone number format';

                                default:
                                  return (
                                    <>
                                      Oeps, er ging iets mis. Onze excuses voor
                                      het ongemak.
                                      <br />
                                      Neem contact met ons op via de{' '}
                                      <a
                                        href={CONTACTS_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bold"
                                      >
                                        contactpagina
                                      </a>
                                      .
                                      <br />
                                      <br />
                                      Oops, something went wrong. We’re sorry
                                      for the inconvenience.
                                      <br />
                                      Please contact us via the{' '}
                                      <a
                                        href={CONTACTS_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bold"
                                      >
                                        contact page
                                      </a>
                                      .
                                    </>
                                  );
                              }
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                </Popup>
              </Transition>
            </Portal>

            <Portal>
              <Transition on={emailPartnerPopup}>
                <Popup>
                  <>
                    <div className="popup-header">
                      <h4>Wijzig het e-mailadres</h4>

                      <button
                        className="popup-close-button"
                        onClick={() => showEmailPartnerPopup(false)}
                      >
                        &times;
                      </button>
                    </div>

                    <div className="popup-body userSettings-popup-body">
                      <label className="userSettings-field-title userSettings-popup-field-title">
                        E-mailadres partner
                        <input
                          ref={providerEmailField}
                          autoFocus
                          className="userSettings-input-field"
                          type="email"
                          onChange={() =>
                            providerEmailField.current.classList.contains(
                              'userSettings-popup-field-warning'
                            ) &&
                            providerEmailField.current.classList.remove(
                              'userSettings-popup-field-warning'
                            )
                          }
                          defaultValue={data.emailPartner}
                        />
                      </label>

                      <button
                        onClick={() => {
                          trackAction(
                            platform,
                            'settings',
                            'clearpartneremail'
                          );

                          setData({
                            ...data,
                            emailPartner: null
                          });

                          changeUserData({
                            variables: {
                              emailPartner: null
                            }
                          });

                          showEmailPartnerPopup(false);
                        }}
                        className="userSettings-button userSettings-button-secondary"
                      >
                        Verwijderen
                      </button>

                      <button
                        onClick={() => {
                          if (!isEmail(providerEmailField.current.value)) {
                            return providerEmailField.current.classList.add(
                              'userSettings-popup-field-warning'
                            );
                          } else {
                            trackAction(
                              platform,
                              'settings',
                              'setpartneremail'
                            );

                            setData({
                              ...data,
                              emailPartner: providerEmailField.current.value
                            });

                            changeUserData({
                              variables: {
                                emailPartner: providerEmailField.current.value
                              }
                            });

                            showEmailPartnerPopup(false);
                          }
                        }}
                        className="userSettings-button"
                      >
                        Wijzigen
                      </button>
                    </div>
                  </>
                </Popup>
              </Transition>
            </Portal>
          </div>
        </div>
      );
  }
}

function UserSettingsItem({ text, onClick }) {
  return (
    <button className="userSettings-field" onClick={onClick}>
      {text}
      <RightArrow className="userSettings-field-icon" />
    </button>
  );
}

function UserSettingsDateItem({
  data,
  date,
  period,
  setData,
  onClick,
  platform,
  initialDate,
  changeUserData,
  changeInitialDate
}) {
  const [datePicker, showDatePicker] = useState(false);

  if (['PREPARING', 'OTHER'].includes(data.status)) return null;

  const [label, value] = getDateAttributes(data);

  return (
    <div className="userSettings-date-field">
      <p className="userSettings-field-title">{label}</p>

      <UserSettingsItem
        onClick={() => {
          if (!data.status) return;

          if (onClick) return onClick();

          if (date)
            changeInitialDate({
              day: new Date(date).getDate(),
              year: new Date(date).getFullYear(),
              month: new Date(date).getMonth() + 1
            });

          showDatePicker(true);
        }}
        text={value ? value.split('-').reverse().join('-') : 'Onbekend'}
      />

      {datePicker && (
        <div className="userSettings-date-picker">
          <DatePickerComponent
            title="Kies de datum"
            period={period}
            initialDate={initialDate}
            showDatePicker={showDatePicker}
            onDateChange={(date) => {
              const formattedDate = formatDate(date);

              const variables =
                data.status === 'PREGNANT'
                  ? { edd: formattedDate }
                  : { pregnancyEndDate: formattedDate };

              trackAction(
                platform,
                'settings',
                data.status === 'PREGNANT' ? 'setedd' : 'setpregnancyenddate'
              );

              changeUserData({
                variables: {
                  ...variables
                }
              });

              setData({
                ...data,
                ...variables
              });
            }}
            changeInitialDate={changeInitialDate}
          />
        </div>
      )}
    </div>
  );
}

function OrganizationCard({ logoUrl, name, distance }) {
  return (
    <div className="userSettings-provider-card">
      <img
        className="userSettings-provider-image"
        width="45"
        height="45"
        src={logoUrl}
        alt="organization-logo"
      />

      <p className="userSettings-provider-card-text">
        {name}

        {distance && (
          <span className="userSettings-provider-card-text-small">
            {distance} km
          </span>
        )}
      </p>
    </div>
  );
}

function getDateAttributes(data) {
  const start = new Date();
  const end = new Date();

  end.setDate(end.getDate() + 40 * 7);
  start.setDate(start.getDate() - 40 * 7);

  switch (data.status) {
    case 'PREGNANT':
      return [
        'Uitgerekende datum',
        data.edd,
        {
          start: new Date(),
          end
        }
      ];
    case 'DELIVERED':
      return [
        'Bevallen op',
        data.pregnancyEndDate,
        {
          start,
          end: new Date()
        }
      ];
    case 'ENDED':
      return [
        'Beëindigd per',
        data.pregnancyEndDate,
        {
          end: new Date()
        }
      ];
    default:
      return [];
  }
}

function formatDate(date) {
  if (!date) return null;

  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - offset * 60 * 1000);

  return date.toISOString().split('T')[0];
}
